import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  Image,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  Badge,
  SimpleGrid,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { IoMdArrowForward, IoMdTrendingUp, IoMdTime } from "react-icons/io";
import { IoShieldCheckmark } from "react-icons/io5";
import { FaBrain, FaHospital } from "react-icons/fa";

import { handleCTA } from "../../../../utils";
// import { useSmoothScroll } from "../../../../utils/smoothScroll";
// import { ANIMATION_CONSTANTS } from "../../../../constants/animations";
import AppStoreButton from "../../AppStoreButton";
import PlayStoreButton from "../../PlayStoreButton";

// Motion components
const MotionBox = motion(Box);
const MotionButton = motion(Button);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionImage = motion(Image);

// Animations
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const fadeInRight = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

interface HeroProps {
  title?: string;
  titleHighlight?: string;
  subtitle?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  primaryButtonAction?: () => void;
  secondaryButtonAction?: () => void;
  heroImage?: string;
  showTrustIndicators?: boolean;
  scrollToSection?: string;
}

function Hero({
  title = "AI Clinical Intelligence",
  titleHighlight = "That Transforms Healthcare",
  subtitle = "Reduce documentation time by 60% while improving accuracy to 99.8%. Highly secured AI designed for healthcare workflows, trusted by leading physicians and healthcare professionals.",
  primaryButtonText = "Start Free Trial",
  secondaryButtonText = "Request Demo",
  primaryButtonAction,
  // secondaryButtonAction,
  showTrustIndicators = true,
}: // scrollToSection: scrollToSectionId,
HeroProps) {
  // const { scrollToSection } = useSmoothScroll();

  // Medical-first color palette
  const bgGradient = useColorModeValue(
    "linear(to-br, slate.50, blue.50, purple.50)",
    "linear(to-br, gray.900, blue.900, purple.900)"
  );

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textPrimary = useColorModeValue("gray.900", "white");
  const textSecondary = useColorModeValue("gray.600", "gray.300");
  const purplePrimary = "#6366F1";
  const greenSuccess = "#10B981";
  const amberWarning = "#F59E0B";
  const iosMockImage = "/assets/images/snapshots/care-timeline.png";
  const androidMockImage = "/assets/images/snapshots/documents.png";

  const floatingAnimationPrimary = {
    animate: { y: [0, -12, 0] },
    transition: { duration: 6, repeat: Infinity, ease: "easeInOut" },
  };

  const floatingAnimationSecondary = {
    animate: { y: [0, 12, 0] },
    transition: {
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut",
      delay: 0.8,
    },
  };

  const handlePrimaryClick = () => {
    if (primaryButtonAction) {
      primaryButtonAction();
    } else {
      handleCTA("signup");
    }
  };

  const handleSecondaryClick = () => {
    handleCTA("signup");
  };

  const trustMetrics = [
    {
      icon: IoShieldCheckmark,
      label: "HIPAA Compliant",
      value: "100%",
      color: greenSuccess,
    },
    {
      icon: IoMdTrendingUp,
      label: "Accuracy Rate",
      value: "99.8%",
      color: purplePrimary,
    },
    {
      icon: IoMdTime,
      label: "Time Saved",
      value: "60%",
      color: amberWarning,
    },
    {
      icon: FaHospital,
      label: "Trusted By",
      value: "500+",
      color: greenSuccess,
    },
  ];

  return (
    <Box
      as="section"
      bgGradient={bgGradient}
      position="relative"
      overflow="hidden"
      minH="100vh"
      display="flex"
      alignItems="center"
    >
      {/* Medical pattern overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.03}
        backgroundImage={`url("data:image/svg+xml,%3csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3e%3cg fill='none' fill-rule='evenodd'%3e%3cg fill='%236366F1' fill-opacity='0.1'%3e%3ccircle cx='30' cy='30' r='2'/%3e%3c/g%3e%3c/g%3e%3c/svg%3e")`}
        backgroundSize="60px 60px"
      />

      <Container
        maxW="container.xl"
        position="relative"
        py={{ base: 16, lg: 20 }}
      >
        <Stack
          direction={{ base: "column", lg: "row" }}
          spacing={{ base: 12, lg: 20 }}
          align="center"
          justify="space-between"
        >
          {/* Left Content */}
          <VStack
            spacing={8}
            align={{ base: "center", lg: "flex-start" }}
            textAlign={{ base: "center", lg: "left" }}
            maxW={{ base: "full", lg: "70%" }}
            flex="1"
          >
            {/* Medical Badge */}
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Badge
                px={4}
                py={2}
                bg={`${purplePrimary}15`}
                color={purplePrimary}
                fontSize="sm"
                fontWeight="600"
                borderRadius="full"
                letterSpacing="wide"
                textTransform="uppercase"
                border={`1px solid ${purplePrimary}30`}
              >
                <HStack spacing={2}>
                  <Icon as={FaBrain} boxSize={4} />
                  <Text>AI-Powered Clinical Intelligence</Text>
                </HStack>
              </Badge>
            </MotionBox>

            {/* Main Headline */}
            <VStack spacing={6} align={{ base: "center", lg: "flex-start" }}>
              <MotionHeading
                size="2xl"
                lineHeight="1.1"
                fontWeight="700"
                color={textPrimary}
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                {...fadeInUp}
              >
                {title}{" "}
                <Text
                  as="span"
                  bgGradient={`linear(to-r, ${purplePrimary}, #8B5CF6)`}
                  bgClip="text"
                  fontWeight="800"
                >
                  {titleHighlight}
                </Text>
              </MotionHeading>

              <MotionText
                fontSize={{ base: "lg", md: "xl" }}
                color={textSecondary}
                lineHeight="1.6"
                maxW="600px"
                {...fadeInUp}
                transition={{ delay: 0.2 }}
              >
                {subtitle}
              </MotionText>
            </VStack>

            {/* CTA Buttons */}
            <MotionBox {...fadeInUp} transition={{ delay: 0.3 }}>
              <HStack
                spacing={4}
                flexWrap="wrap"
                justify={{ base: "center", lg: "flex-start" }}
              >
                <MotionButton
                  size="lg"
                  bg={purplePrimary}
                  color="white"
                  _hover={{
                    bg: "#5855EB",
                    transform: "translateY(-2px)",
                    boxShadow: "0 10px 25px rgba(99, 102, 241, 0.3)",
                  }}
                  _active={{ transform: "translateY(0)" }}
                  borderRadius="xl"
                  px={8}
                  py={6}
                  fontWeight="600"
                  fontSize="md"
                  rightIcon={<IoMdArrowForward />}
                  onClick={handlePrimaryClick}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                  boxShadow="0 4px 14px rgba(99, 102, 241, 0.25)"
                >
                  {primaryButtonText}
                </MotionButton>

                <MotionButton
                  size="lg"
                  variant="outline"
                  borderColor={borderColor}
                  color={textPrimary}
                  _hover={{
                    bg: `${purplePrimary}08`,
                    borderColor: purplePrimary,
                    transform: "translateY(-2px)",
                  }}
                  borderRadius="xl"
                  px={8}
                  py={6}
                  fontWeight="600"
                  fontSize="md"
                  onClick={handleSecondaryClick}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                >
                  {secondaryButtonText}
                </MotionButton>
              </HStack>
            </MotionBox>

            {/* Trust Indicators */}
            {!showTrustIndicators && (
              <MotionBox w="full" {...fadeInUp} transition={{ delay: 0.4 }}>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} w="full">
                  {trustMetrics.map((metric, idx) => (
                    <VStack
                      key={idx}
                      spacing={2}
                      p={4}
                      bg={cardBg}
                      borderRadius="xl"
                      border={`1px solid ${borderColor}`}
                      boxShadow="0 2px 8px rgba(0, 0, 0, 0.04)"
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
                      }}
                      transition="all 0.3s ease"
                    >
                      <Icon as={metric.icon} boxSize={6} color={metric.color} />
                      <Text fontSize="2xl" fontWeight="700" color={textPrimary}>
                        {metric.value}
                      </Text>
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        color={textSecondary}
                        textTransform="uppercase"
                        letterSpacing="wide"
                        textAlign="center"
                      >
                        {metric.label}
                      </Text>
                    </VStack>
                  ))}
                </SimpleGrid>
              </MotionBox>
            )}
            <VStack
              spacing={3}
              align={{ base: "center", md: "center" }}
              textAlign={{ base: "center", md: "center" }}
              mt={{ base: 8, md: 12 }}
            >
              <HStack
                spacing={4}
                flexWrap="wrap"
                justify={{ base: "center", md: "center" }}
              >
                <AppStoreButton />
                <PlayStoreButton />
              </HStack>
            </VStack>
          </VStack>

          {/* Right Visual */}
          <MotionBox
            flex="1"
            minW={{ base: "100%", lg: "50%" }}
            {...fadeInRight}
            transition={{ delay: 0.2 }}
          >
            <Box
              position="relative"
              borderRadius="2xl"
              overflow="hidden"
              // boxShadow="0 20px 60px rgba(0, 0, 0, 0.1)"
              // bg={cardBg}
              // border={`1px solid ${borderColor}`}
            >
              <Box
                p={{ base: 6, md: 10 }}
                minH={{ base: "460px", md: "540px" }}
                position="relative"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                justifyContent="flex-end"
              >
                <Box
                  position="absolute"
                  inset={4}
                  bg="whiteAlpha.200"
                  borderRadius="2xl"
                />
                <Box position="relative" w="full" flex="1" minH="640px">
                  <MotionBox
                    position="absolute"
                    top={{ base: "5%", md: "2%" }}
                    left={{ base: "5%", md: "10%" }}
                    transform="translateX(-50%)"
                    {...floatingAnimationPrimary}
                  >
                    <Box
                      w={{ base: "265", md: "265" }}
                      borderRadius="3xl"
                      bg="white"
                      p={3}
                      boxShadow="0 20px 40px rgba(0, 0, 0, 0.25)"
                      border={`1px solid ${borderColor}`}
                    >
                      <MotionImage
                        src={iosMockImage}
                        alt="QiQ Health mobile app preview"
                        borderRadius="2xl"
                        objectFit="contain"
                        objectPosition="center"
                        w="100%"
                        h="420px"
                        backgroundColor="white"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                      />
                    </Box>
                  </MotionBox>

                  <MotionBox
                    position="absolute"
                    bottom={{ base: "0%", md: "8%" }}
                    right={{ base: "5%", md: "10%" }}
                    {...floatingAnimationSecondary}
                  >
                    <Box
                      w={{ base: "230px", md: "230px" }}
                      borderRadius="3xl"
                      bg="white"
                      p={3}
                      boxShadow="0 24px 50px rgba(0, 0, 0, 0.2)"
                      border={`1px solid ${borderColor}`}
                    >
                      <MotionImage
                        src={androidMockImage}
                        alt="QiQ Health Android app preview"
                        borderRadius="2xl"
                        objectFit="contain"
                        objectPosition="center"
                        w="100%"
                        h="420px"
                        backgroundColor="white"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                      />
                    </Box>
                  </MotionBox>
                </Box>
              </Box>
            </Box>
          </MotionBox>
        </Stack>
      </Container>
    </Box>
  );
}

export default Hero;
